import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './CourtUsageStatistics.css';

/**
 * 统计页面组件
 * 
 * @param {Object} props
 * @param {Function} props.onRefresh - 刷新回调
 */
const CourtUsageStatistics = ({ onRefresh }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: ''
  });

  const baseUrl = getApiBaseUrl();

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (filters.startDate) {
        params.startDate = filters.startDate;
      }
      if (filters.endDate) {
        params.endDate = filters.endDate;
      }

      const response = await axios.get(`${baseUrl}/api/court-usage/statistics`, { params });
      
      if (response.data.success) {
        setStatistics(response.data.data);
      } else {
        throw new Error(response.data.message || '获取统计数据失败');
      }
    } catch (err) {
      console.error('获取统计数据失败:', err);
      setError(err.response?.data?.message || err.message || '获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleRefresh = () => {
    fetchStatistics();
    if (onRefresh) {
      onRefresh();
    }
  };

  if (loading) {
    return (
      <div className="statistics-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="statistics-page">
        <div className="error-message">{error}</div>
        <button className="refresh-button" onClick={handleRefresh}>重试</button>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="statistics-page">
        <div className="empty-state">暂无统计数据</div>
      </div>
    );
  }

  return (
    <div className="statistics-page">
      {/* 头部 */}
      <div className="statistics-header">
        <h2>统计信息</h2>
        <button className="refresh-button" onClick={handleRefresh} title="刷新">
          🔄
        </button>
      </div>

      {/* 筛选器 */}
      <div className="filters-container">
        <div className="filter-group">
          <label>开始日期</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>结束日期</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>

        {(filters.startDate || filters.endDate) && (
          <button 
            className="clear-filter-button"
            onClick={() => setFilters({ startDate: '', endDate: '' })}
          >
            清除筛选
          </button>
        )}
      </div>

      {/* 主账户统计 */}
      <div className="statistics-section">
        <h3 className="section-title">主账户统计</h3>
        <div className="stats-card">
          <div className="stat-item">
            <span className="stat-label">总充值</span>
            <span className="stat-value positive">
              {statistics.mainAccount?.totalRecharged?.toFixed(2) || '0.00'} 场时
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">总消费</span>
            <span className="stat-value negative">
              {statistics.mainAccount?.totalConsumed?.toFixed(2) || '0.00'} 场时
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">当前余额</span>
            <span className={`stat-value ${statistics.mainAccount?.currentBalance < 0 ? 'negative' : 'positive'}`}>
              {statistics.mainAccount?.currentBalance?.toFixed(2) || '0.00'} 场时
            </span>
          </div>
        </div>

        {/* 按场地统计 */}
        {statistics.mainAccountByCourt && statistics.mainAccountByCourt.length > 0 && (
          <div className="court-stats">
            <h4 className="subsection-title">按场地统计</h4>
            <div className="court-stats-grid">
              {statistics.mainAccountByCourt.map(court => (
                <div key={court.courtId} className="court-stat-card">
                  <div className="court-stat-name">{court.courtName}</div>
                  <div className="court-stat-details">
                    <div className="court-stat-item">
                      <span>充值:</span>
                      <span className="positive">{court.totalRecharged.toFixed(2)} 场时</span>
                    </div>
                    <div className="court-stat-item">
                      <span>消费:</span>
                      <span className="negative">{court.totalConsumed.toFixed(2)} 场时</span>
                    </div>
                    <div className="court-stat-item">
                      <span>余额:</span>
                      <span className={court.currentBalance < 0 ? 'negative' : 'positive'}>
                        {court.currentBalance.toFixed(2)} 场时
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 子账户统计 */}
      {statistics.subAccounts && statistics.subAccounts.length > 0 && (
        <div className="statistics-section">
          <h3 className="section-title">子账户统计</h3>
          <div className="sub-account-stats-table">
            <table>
              <thead>
                <tr>
                  <th>账户名称</th>
                  <th>总分配</th>
                  <th>总消费</th>
                  <th>当前余额</th>
                </tr>
              </thead>
              <tbody>
                {statistics.subAccounts.map(account => (
                  <tr key={account.accountId}>
                    <td>{account.accountName}</td>
                    <td className="positive">{account.totalAllocated?.toFixed(2) || '0.00'} 场时</td>
                    <td className="negative">{account.totalConsumed?.toFixed(2) || '0.00'} 场时</td>
                    <td className={account.currentBalance < 0 ? 'negative' : 'positive'}>
                      {account.currentBalance?.toFixed(2) || '0.00'} 场时
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 使用趋势 */}
      {statistics.usageTrend && statistics.usageTrend.length > 0 && (
        <div className="statistics-section">
          <h3 className="section-title">使用趋势</h3>
          <div className="trend-chart">
            <div className="trend-list">
              {statistics.usageTrend.map((item, index) => (
                <div key={index} className="trend-item">
                  <div className="trend-date">
                    {new Date(item.date).toLocaleDateString('zh-CN')}
                  </div>
                  <div className="trend-bar-container">
                    <div 
                      className="trend-bar"
                      style={{ 
                        width: `${Math.min(100, (item.consumed / Math.max(...statistics.usageTrend.map(t => t.consumed))) * 100)}%` 
                      }}
                    >
                      <span className="trend-value">{item.consumed.toFixed(2)} 场时</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {(!statistics.subAccounts || statistics.subAccounts.length === 0) && 
       (!statistics.usageTrend || statistics.usageTrend.length === 0) && (
        <div className="empty-state">
          <p>暂无统计数据</p>
        </div>
      )}
    </div>
  );
};

export default CourtUsageStatistics;

